import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5173';

export interface Dashboard {
  id: string;
  userId: string;
  tenantId: string;
  name: string;
  description?: string;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
  pages: DashboardPage[];
}

export interface DashboardPage {
  id: string;
  dashboardId: string;
  pageNumber: number;
  name: string;
  widgets: Widget[];
}

export interface Widget {
  id: string;
  pageId: string;
  widgetType: 'metric' | 'chart' | 'text' | 'table' | 'media' | 'iframe';
  x: number;
  y: number;
  w: number;
  h: number;
  minW: number;
  minH: number;
  config: string; // JSON string
}

class DashboardService {
  // Helper to get headers with mock user context
  private getHeaders() {
    const storedUser = localStorage.getItem('auth_user');
    const currentDomainId = localStorage.getItem('swm-current-workspace-id');
    const headers: Record<string, string> = {};

    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        headers['X-Mock-User'] = JSON.stringify({
          id: user.id,
          email: user.email,
          tenantId: user.tenantId,
          domainId: currentDomainId || undefined,
          role: user.role || 'member'
        });
      } catch {
        // Ignore
      }
    }
    return headers;
  }

  // List dashboards
  async list(page = 1, limit = 20, search?: string) {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });

    const response = await axios.get(`${API_URL}/api/shell/dashboards?${params}`, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Create dashboard
  async create(data?: { name?: string; description?: string; isDefault?: boolean }) {
    const response = await axios.post(`${API_URL}/api/shell/dashboards`, data || {}, {
      headers: this.getHeaders()
    });
    return response.data as Dashboard;
  }

  // Get dashboard by ID
  async get(id: string) {
    const response = await axios.get(`${API_URL}/api/shell/dashboards/${id}`, {
      headers: this.getHeaders()
    });
    return response.data as Dashboard;
  }

  // Update dashboard
  async update(id: string, data: { name?: string; description?: string }) {
    const response = await axios.put(`${API_URL}/api/shell/dashboards/${id}`, data, {
      headers: this.getHeaders()
    });
    return response.data as Dashboard;
  }

  // Delete dashboard
  async delete(id: string) {
    await axios.delete(`${API_URL}/api/shell/dashboards/${id}`, {
      headers: this.getHeaders()
    });
  }

  // Duplicate dashboard
  async duplicate(id: string): Promise<{ newDashboardId: string; newDashboardName: string; pagesCopied: number; widgetsCopied: number }> {
    const response = await axios.post(`${API_URL}/api/shell/dashboards/${id}/duplicate`, {}, {
      headers: this.getHeaders()
    });
    return response.data;
  }

  // Set default dashboard
  async setDefault(id: string) {
    const response = await axios.patch(`${API_URL}/api/shell/dashboards/${id}/default`, {}, {
      headers: this.getHeaders()
    });
    return response.data as Dashboard;
  }

  // Add page
  async addPage(dashboardId: string, name?: string) {
    const response = await axios.post(`${API_URL}/api/shell/dashboards/${dashboardId}/pages`, { name }, {
      headers: this.getHeaders()
    });
    return response.data as DashboardPage;
  }

  // Delete page
  async deletePage(pageId: string) {
    await axios.delete(`${API_URL}/api/shell/pages/${pageId}`, {
      headers: this.getHeaders()
    });
  }

  // Update page
  async updatePage(pageId: string, name: string) {
    const response = await axios.put(`${API_URL}/api/shell/pages/${pageId}`, { name }, {
      headers: this.getHeaders()
    });
    return response.data as DashboardPage;
  }

  // Duplicate page (Story 2.18)
  async duplicatePage(pageId: string): Promise<{ newPageId: string; newPageName: string; widgetsCopied: number }> {
    const response = await axios.post(`${API_URL}/api/shell/pages/${pageId}/duplicate`);
    return response.data;
  }

  // Import dashboard (Story 2.20)
  async importDashboard(exportData: any): Promise<{ dashboardId: string; message: string }> {
    // Transform export data to import request format
    const importRequest = {
      name: exportData.dashboard.name,
      description: exportData.dashboard.description,
      pages: exportData.pages.map((page: any) => ({
        name: page.name,
        pageNumber: page.pageNumber,
        widgets: page.widgets.map((widget: any) => ({
          widgetType: widget.widgetType,
          x: widget.x,
          y: widget.y,
          w: widget.w,
          h: widget.h,
          minW: widget.minW,
          minH: widget.minH,
          config: typeof widget.config === 'string' ? widget.config : JSON.stringify(widget.config)
        }))
      }))
    };

    const response = await axios.post(`${API_URL}/api/shell/dashboards/import`, importRequest);
    return response.data;
  }

  // Get or create module dashboard (Story 6.2)
  async getOrCreateModuleDashboard(moduleId: string): Promise<Dashboard> {
    const response = await axios.get(`${API_URL}/api/shell/modules/${moduleId}/dashboard`);
    return response.data as Dashboard;
  }
}

export const dashboardService = new DashboardService();
